'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { type ComponentType, type MouseEvent, useRef, useState } from 'react';
import { OptimizedImage } from '@/components/images';
import { AnimatedButton } from '@/components/ui/animated-button';
import { buildAppDownloadUrl, isIPhone } from '@/config/app-upsells';
import { onEvent } from '@/events';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { appUpsellProperties, warnMissingAttributionLink } from '@/lib/app-upsells/analytics';
import { env } from '@/lib/config/env';
import type { AppDownloadDialogProps } from './app-download-dialog';
import { useUpsellImpression } from './use-upsell-impression';

const AppDownloadDialog: ComponentType<AppDownloadDialogProps> = dynamic(
  () => import('./app-download-dialog').then((module) => module.AppDownloadDialog),
  // Keep the first lazy mount inside this boundary, not the route's loading fallback.
  { loading: () => null },
);

interface AppDownloadActionProps {
  surface: 'feed' | 'navigation' | 'portfolio';
  onAfterAction?: (() => void) | undefined;
}

export function AppDownloadAction(props: AppDownloadActionProps): React.ReactNode {
  const rolloutEnabled = useFeatureGate('app_upsells') === true;
  const enabled = props.surface === 'portfolio' || rolloutEnabled;
  return enabled ? <EnabledAppDownloadAction {...props} /> : null;
}

function EnabledAppDownloadAction({
  surface,
  onAfterAction,
}: AppDownloadActionProps): React.ReactNode {
  const t = useTranslations('app_upsells');
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const ref = useUpsellImpression('navigation', surface === 'navigation');

  function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
    const modified =
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1;
    const direct = isIPhone(navigator.userAgent) || modified;
    onEvent('app_upsell_clicked', {
      ...appUpsellProperties(surface, surface),
      destination: direct ? 'app_store' : 'dialog',
    });
    warnMissingAttributionLink();
    if (modified) return;
    if (direct) {
      onAfterAction?.();
      return;
    }
    event.preventDefault();
    setLoaded(true);
    setOpen(true);
  }

  return (
    <div
      ref={ref}
      className={
        surface === 'navigation'
          ? 'w-full'
          : surface === 'portfolio'
            ? 'w-full sm:w-auto'
            : 'shrink-0'
      }
    >
      <AnimatedButton
        asChild
        variant={surface === 'navigation' ? 'tertiary' : 'primary'}
        size={surface === 'portfolio' ? 'default' : 'lg'}
        disableHoverScale
        className={
          surface === 'navigation'
            ? 'h-auto min-h-14 w-full justify-start gap-3 px-3 py-2 text-left'
            : surface === 'portfolio'
              ? 'w-full rounded-panel-row sm:w-auto'
              : undefined
        }
      >
        <a
          ref={triggerRef}
          href={buildAppDownloadUrl(env.NEXT_PUBLIC_APPSFLYER_ONELINK_URL, surface, 'direct')}
          onClick={handleClick}
          onAuxClick={(event: MouseEvent<HTMLAnchorElement>): void => {
            if (event.button === 1) handleClick(event);
          }}
          data-testid={`app-download-${surface}`}
        >
          {surface === 'navigation' ? (
            <>
              <OptimizedImage
                variant="thumbnail"
                src="/icons/morfi-logomark.svg"
                alt=""
                size="size-9"
                priority
                preview={false} // ALLOW-NO-PREVIEW: decorative app promotion artwork, not user-uploaded media
                // ALLOW-NSFW-FALSE: reviewed first-party app artwork, not user-uploaded media
                nsfw={false}
                loadReveal="none"
              />
              <span className="min-w-0 whitespace-normal">
                <span className="block text-heading-xs">{t('name')}</span>
                <span className="block text-caption font-normal text-foreground-secondary">
                  {t('free')}
                </span>
              </span>
            </>
          ) : (
            t('get_app')
          )}
        </a>
      </AnimatedButton>
      {loaded ? (
        <AppDownloadDialog
          open={open}
          onOpenChange={setOpen}
          entryPoint={surface}
          returnFocusRef={triggerRef}
        />
      ) : null}
    </div>
  );
}
